/**
 * Relative date formatting for notifications.
 * Uses date-fns formatRelative with Israel locale (Sunday–Saturday week, 24h time).
 * Language-aware: switches between English and Hebrew tokens based on i18n language.
 */
import { formatRelative, parseISO, isValid, type Locale } from 'date-fns';
import { enUS, he } from 'date-fns/locale';
import i18n from '@/i18n';

// ─── English tokens ───

const enFullTokens: Record<string, string> = {
  lastWeek: "'Last' eeee 'at' HH:mm",
  yesterday: "'Yesterday at' HH:mm",
  today: "'Today at' HH:mm",
  tomorrow: "'Tomorrow at' HH:mm",
  nextWeek: "'Next' eeee 'at' HH:mm",
  other: 'dd/MM/yyyy HH:mm',
};

const enDateOnlyTokens: Record<string, string> = {
  lastWeek: "'Last' eeee",
  yesterday: "'Yesterday'",
  today: "'Today'",
  tomorrow: "'Tomorrow'",
  nextWeek: "'Next' eeee",
  other: 'dd/MM/yyyy',
};

// ─── Hebrew tokens ───

const heFullTokens: Record<string, string> = {
  lastWeek: "eeee 'שעבר ב'HH:mm",
  yesterday: "'אתמול ב'HH:mm",
  today: "'היום ב'HH:mm",
  tomorrow: "'מחר ב'HH:mm",
  nextWeek: "eeee 'הבא ב'HH:mm",
  other: 'dd/MM/yyyy HH:mm',
};

const heDateOnlyTokens: Record<string, string> = {
  lastWeek: "eeee 'שעבר'",
  yesterday: "'אתמול'",
  today: "'היום'",
  tomorrow: "'מחר'",
  nextWeek: "eeee 'הבא'",
  other: 'dd/MM/yyyy',
};

// ─── Locale builders ───

function buildLocale(baseLocale: Locale, tokens: Record<string, string>) {
  return {
    ...baseLocale,
    options: { ...baseLocale.options, weekStartsOn: 0 as const },
    formatRelative: (token: string) => tokens[token] || tokens.other,
  };
}

function isHebrew(): boolean {
  return i18n.language === 'he';
}

// ─── Full relative (with time) — for notification timestamps ───

/**
 * Format a date relative to now — includes time.
 * e.g. EN: "Today at 14:30", "Yesterday at 09:00", "Last Monday at 15:45"
 * e.g. HE: "היום ב14:30", "אתמול ב09:00", "שני שעבר ב15:45"
 */
export function formatRelativeDateTime(date: Date | string): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(dateObj)) return typeof date === 'string' ? date : '';
  const hebrew = isHebrew();
  const locale = buildLocale(hebrew ? he : enUS, hebrew ? heFullTokens : enFullTokens);
  return formatRelative(dateObj, new Date(), { locale: locale as Parameters<typeof formatRelative>[2]['locale'] });
}

// ─── Date-only relative — for dates inside messages ───

/**
 * Format a date relative to now — date only, no time.
 * e.g. EN: "Today", "Yesterday", "Last Monday", "Next Sunday", "02/02/2026"
 * e.g. HE: "היום", "אתמול", "שני שעבר", "ראשון הבא", "02/02/2026"
 */
export function formatRelativeDateOnly(date: Date | string): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(dateObj)) return typeof date === 'string' ? date : '';
  const hebrew = isHebrew();
  const locale = buildLocale(hebrew ? he : enUS, hebrew ? heDateOnlyTokens : enDateOnlyTokens);
  return formatRelative(dateObj, new Date(), { locale: locale as Parameters<typeof formatRelative>[2]['locale'] });
}

/**
 * Replace a YYYY-MM-DD date string inside a notification message
 * with its relative form (e.g. "2026-02-02" → "today" / "היום").
 * Preserves any trailing time range like "12:30-13:30".
 */
export function replaceMessageDate(message: string): string {
  return message.replace(
    /(\d{4}-\d{2}-\d{2})(\s+\d{1,2}:\d{2}-\d{1,2}:\d{2})?/g,
    (_match, dateStr: string, timeRange?: string) => {
      const relative = formatRelativeDateOnly(dateStr);
      return timeRange ? `${relative}${timeRange}` : relative;
    },
  );
}
